// storyOverlay.js
// Client-side mirror of the server shiftStory template's text/stat logic
// (functions/social/templates/shiftStory.js). Used by the live ANIMATED preview
// so its overlay matches the static server-rendered Story exactly. Stats are
// derived from the WHOLE shift (count, custom/flash, fastest/longest, artists) —
// selection only changes which pancakes drift/compose, not the numbers.

const mmss = (ms) => ms == null ? null
  : `${Math.floor(ms / 1000 / 60)}:${String(Math.round(ms / 1000) % 60).padStart(2, "0")}`;
const titleCase = (s) => String(s || "").toLowerCase()
  .replace(/\b\w/g, (c) => c.toUpperCase()).replace(/@\s*/, "@ ");

export function splitName(n) {
  const t = titleCase(n);
  if (t.includes("@")) { const [a, b] = t.split("@"); return [a.trim(), "@ " + b.trim()]; }
  const w = t.split(" ");
  if (w.length > 3) { const m = Math.ceil(w.length / 2); return [w.slice(0, m).join(" "), w.slice(m).join(" ")]; }
  return [t, ""];
}
function fmtDate(ms) {
  if (ms == null) return "";
  return new Date(ms).toLocaleDateString("en-US",
    { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "America/Chicago" });
}
function artistPill(arts) {
  const a = [...new Set(arts.filter(Boolean))];
  if (a.length === 0) return { big: "The booth", sub: "live art" };
  if (a.length === 1) return { big: a[0], sub: "solo set" };
  if (a.length === 2) return { big: a.join(" + "), sub: "on the griddle" };
  return { big: a.length + " artists", sub: a.slice(0, 3).join(", ") };
}

// Returns the overlay model shared by static + animated: header lines, date,
// the big count, and the 4 stat pills.
export function computeOverlay(recap) {
  const pancakes = (recap?.pancakes || []).filter((p) => p.cutoutUrl || p.thumbUrl);
  const [l1, l2] = splitName(recap?.eventName || recap?.venueName || "Pancake Shift");
  // Shift date = when it OPENED. Night shifts close after midnight, and a
  // Saturday-night gig labeled "Sunday" reads wrong (Dan, 2026-06-11).
  const date = fmtDate(recap?.openedAtMs ?? recap?.closedAtMs);
  const count = recap?.pancakeCount ?? pancakes.length;
  const customCount = pancakes.filter((p) => p.orderType === "custom").length;
  const flashCount = pancakes.length - customCount;
  const draws = pancakes.map((p) => p.drawTimeMs).filter((v) => v != null);
  const fast = draws.length ? mmss(Math.min(...draws)) : null;
  const long = draws.length ? mmss(Math.max(...draws)) : null;
  const artists = pancakes.map((p) => p.artistName).filter(Boolean);

  const pills = [artistPill(artists), { big: customCount + " custom", sub: "+ " + flashCount + " flash" }];
  if (fast) pills.push({ big: "⚡ " + fast, sub: "fastest draw", gold: true });
  if (long) pills.push({ big: long, sub: "longest draw", gold: true });

  return { l1, l2, date, count, pills };
}

// Default auto-selection (mirrors server pickPancakes): each artist's longest
// draw first (coverage), then fill by global longest, hero = overall longest.
export function autoPick(pancakes, n = 4) {
  const withCut = (pancakes || []).filter((p) => p.cutoutUrl || p.thumbUrl);
  const ranked = [...withCut].sort((a, b) => (b.drawTimeMs ?? -1) - (a.drawTimeMs ?? -1));
  const byArtist = {};
  ranked.forEach((p) => { const k = p.artistName || "?"; (byArtist[k] = byArtist[k] || []).push(p); });
  const picks = [], seen = new Set();
  Object.values(byArtist).forEach((arr) => { if (arr[0]) { picks.push(arr[0]); seen.add(arr[0].orderId); } });
  for (const p of ranked) { if (picks.length >= n) break; if (!seen.has(p.orderId)) { picks.push(p); seen.add(p.orderId); } }
  return picks.slice(0, n).map((p) => p.orderId);
}
